// ============================================================================
// useUserRoleHistory.js — CRUD hook for user_role_history table
// ============================================================================
// WHERE TO PLACE: src/hooks/useUserRoleHistory.js
// ============================================================================
// Dedicated hook for the Role History timeline editor.
// Fetches, adds, updates, and deletes role history rows via Supabase RPCs.
// Computes gaps in coverage for UI warnings.
// NEW: contracted_hours per period + getContractedHoursForMonth() lookup.
//
// Conventions:
//   DB: snake_case  |  UI: camelCase
//   All writes go through requireSession() for RLS compliance
//   Mutations return { success, error? }
// ============================================================================

import { useState, useEffect, useCallback, useMemo } from 'react';
import { supabase } from '../supabaseClient';
import { formatSupabaseError } from './useSupabase';

// ============================================================================
// DEBUG FLAG
// ============================================================================
const DEBUG_RH = false;

function debugLog(...args) {
  if (DEBUG_RH) console.log('[useUserRoleHistory]', ...args);
}

// ============================================================================
// SESSION GUARD
// ============================================================================
const requireSession = async () => {
  const { data: { session }, error } = await supabase.auth.getSession();
  if (error) throw new Error('Authentication error: ' + error.message);
  if (!session?.user) throw new Error('Please sign in to perform this action.');
  return session.user.id;
};

// ============================================================================
// NORMALIZER: snake_case -> camelCase
// ============================================================================
function normalizeRoleHistoryRow(row) {
  if (!row) return null;
  return {
    id:              row.id,
    userId:          row.user_id,
    effectiveFrom:   row.effective_from,
    effectiveTo:     row.effective_to || null,
    jobRole:         row.job_role || '',
    jobLevel:        row.job_level ?? 0,
    officeLocation:  row.office_location || null,
    contractedHours: row.contracted_hours ?? null,   // NEW: per-period hours
    loggingMode:     row.logging_mode || 'self',      // 'self' | 'freelancer' | 'auto_allocated'
    isFreelancer:    row.logging_mode === 'freelancer',
    autoAllocatedProjectId: row.auto_allocated_project_id ?? null,
    autoAllocatedTaskId:    row.auto_allocated_task_id ?? null,
    nlSchaal:        row.nl_schaal || null,           // NL CAO schaal ("1"-"10", "c_1"-"c_4")
    nlTrede:         row.nl_trede ?? null,            // NL CAO trede (0-9)
    source:          row.source || 'manual',
    confidence:      row.confidence || 'high',
    notes:           row.notes || '',
    createdAt:       row.created_at,
    createdBy:       row.created_by || null,
  };
}

// ============================================================================
// MONTH OVERLAP UTILITY
// ============================================================================
// Given a target month (YYYY-MM-DD or YYYY-MM), check if a role history
// entry's [effectiveFrom, effectiveTo] overlaps that month.
//
// Month range: first day of month to last day of month.
// Entry overlaps if: effectiveFrom <= lastDayOfMonth AND
//                    (effectiveTo IS NULL OR effectiveTo >= firstDayOfMonth)
// ============================================================================

/**
 * Parse YYYY-MM-DD or YYYY-MM into { year, month } integers.
 * Pure string parsing — no Date constructor — to avoid timezone shifts.
 */
function parseYearMonth(dateStr) {
  if (!dateStr) return null;
  const m = String(dateStr).trim().match(/^(\d{4})-(\d{1,2})/);
  if (!m) return null;
  return { year: parseInt(m[1], 10), month: parseInt(m[2], 10) };
}

/**
 * Get last day of a month as YYYY-MM-DD string.
 */
function lastDayOfMonth(year, month) {
  // Day 0 of next month = last day of current month
  const d = new Date(Date.UTC(year, month, 0));
  return d.toISOString().split('T')[0];
}

/**
 * Get first day of a month as YYYY-MM-DD string.
 */
function firstDayOfMonth(year, month) {
  const mm = String(month).padStart(2, '0');
  return `${year}-${mm}-01`;
}

/**
 * Check if a role history entry overlaps a target month.
 */
function entryOverlapsMonth(entry, monthDate) {
  const ym = parseYearMonth(monthDate);
  if (!ym) return false;

  const monthStart = firstDayOfMonth(ym.year, ym.month);
  const monthEnd = lastDayOfMonth(ym.year, ym.month);

  const from = entry.effectiveFrom;
  const to = entry.effectiveTo; // null = open-ended (current)

  if (!from) return false;
  if (from > monthEnd) return false;
  if (to && to < monthStart) return false;

  return true;
}

// ============================================================================
// GAP COMPUTATION
// ============================================================================
function computeGaps(rows) {
  if (!rows || rows.length === 0) return [];

  const sorted = [...rows].sort((a, b) =>
    a.effectiveFrom.localeCompare(b.effectiveFrom)
  );

  const gaps = [];

  for (let i = 0; i < sorted.length - 1; i++) {
    const currentEnd = sorted[i].effectiveTo;
    const nextStart = sorted[i + 1].effectiveFrom;

    if (!currentEnd) continue;

    const dayAfterEnd = addDays(currentEnd, 1);
    if (dayAfterEnd < nextStart) {
      gaps.push({
        from: dayAfterEnd,
        to: addDays(nextStart, -1),
      });
    }
  }

  return gaps;
}

function addDays(dateStr, days) {
  const d = new Date(dateStr + 'T00:00:00Z');
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().split('T')[0];
}

// ============================================================================
// DEFAULT CONTRACTED HOURS
// ============================================================================
const DEFAULT_CONTRACTED_HOURS = 176;

// ============================================================================
// MAIN HOOK
// ============================================================================
export function useUserRoleHistory(userId) {
  const [roleHistory, setRoleHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // --- Fetch ---
  const fetchRoleHistory = useCallback(async () => {
    if (!userId) {
      setRoleHistory([]);
      return;
    }

    setLoading(true);
    setError(null);
    debugLog('Fetching role history for userId:', userId);

    try {
      const { data, error: fetchError } = await supabase
        .from('user_role_history')
        .select('*')
        .eq('user_id', userId)
        .order('effective_from', { ascending: false });

      if (fetchError) throw fetchError;

      const normalized = (data || []).map(normalizeRoleHistoryRow);
      debugLog('Fetched', normalized.length, 'rows');
      setRoleHistory(normalized);
    } catch (err) {
      const msg = formatSupabaseError(err, { action: 'fetch', entity: 'role history' });
      console.error('[useUserRoleHistory] Fetch error:', err);
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    fetchRoleHistory();
  }, [fetchRoleHistory]);

  // --- Computed: gaps ---
  const gaps = useMemo(() => computeGaps(roleHistory), [roleHistory]);

  // --- Computed: has current row ---
  const hasCurrentRow = useMemo(
    () => roleHistory.some(r => r.effectiveTo === null),
    [roleHistory]
  );

  // ===========================================================================
  // NEW: getContractedHoursForMonth
  // ===========================================================================
  // Client-side lookup: find the role history entry overlapping monthDate,
  // return its contractedHours. Falls back to userDefaultHours or 176.
  //
  // Usage:
  //   const ch = getContractedHoursForMonth('2026-03-01', user.contractedHours);
  // ===========================================================================
  const getContractedHoursForMonth = useCallback((monthDate, userDefaultHours) => {
    if (!roleHistory || roleHistory.length === 0) {
      debugLog('getContractedHoursForMonth: no role history, using fallback', userDefaultHours || DEFAULT_CONTRACTED_HOURS);
      return userDefaultHours || DEFAULT_CONTRACTED_HOURS;
    }

    // Sort ascending for consistent "most recent overlapping" selection
    const sorted = [...roleHistory].sort((a, b) =>
      a.effectiveFrom.localeCompare(b.effectiveFrom)
    );

    // Find all entries overlapping the target month, pick most recent
    const overlapping = sorted.filter(entry => entryOverlapsMonth(entry, monthDate));

    if (overlapping.length === 0) {
      debugLog('getContractedHoursForMonth: no entry overlaps', monthDate, '- gap, using fallback');
      return userDefaultHours || DEFAULT_CONTRACTED_HOURS;
    }

    // Most recent overlapping entry (last in ascending sort)
    const best = overlapping[overlapping.length - 1];
    const hours = best.contractedHours ?? userDefaultHours ?? DEFAULT_CONTRACTED_HOURS;

    debugLog('getContractedHoursForMonth:', monthDate, '->', hours, 'from entry', best.id, best.effectiveFrom);
    return hours;
  }, [roleHistory]);

  // --- Add ---
  const addRoleHistoryRow = useCallback(async (payload) => {
    debugLog('Adding row:', payload);
    try {
      await requireSession();

      const { data, error: rpcError } = await supabase.rpc('role_history_add_row', {
        p_user_id:          payload.userId ?? userId,
        p_effective_from:   payload.effectiveFrom,
        p_effective_to:     payload.effectiveTo || null,
        p_job_role:         payload.jobRole,
        p_job_level:        payload.jobLevel ?? 0,
        p_office_location:  payload.officeLocation || null,
        p_contracted_hours: payload.contractedHours ?? null,
        p_source:           payload.source || 'manual',
        p_confidence:       payload.confidence || 'high',
        p_notes:            payload.notes || null,
        p_nl_schaal:        payload.nlSchaal || null,
        p_nl_trede:         payload.nlTrede ?? null,
        p_logging_mode:     payload.loggingMode || (payload.isFreelancer ? 'freelancer' : 'self'),
        p_auto_allocated_project_id: payload.autoAllocatedProjectId ?? null,
        p_auto_allocated_task_id:    payload.autoAllocatedTaskId ?? null,
      });

      if (rpcError) throw rpcError;

      const result = data;
      if (!result?.success) {
        return { success: false, error: result?.error || 'Unknown RPC error' };
      }

      debugLog('Added row id:', result.id);
      await fetchRoleHistory();
      return { success: true, id: result.id };
    } catch (err) {
      const isOverlap = err?.message?.includes('exclusion constraint') || err?.message?.includes('daterange_excl');
      const msg = isOverlap
        ? 'Date range overlaps with another role history entry. Close the existing entry first or choose non-overlapping dates.'
        : formatSupabaseError(err, { action: 'add', entity: 'role history row' });
      console.error('[useUserRoleHistory] Add error:', err);
      return { success: false, error: msg };
    }
  }, [userId, fetchRoleHistory]);

  // --- Update ---
  const updateRoleHistoryRow = useCallback(async (id, payload) => {
    debugLog('Updating row:', id, payload);
    try {
      await requireSession();

      const { data, error: rpcError } = await supabase.rpc('role_history_update_row', {
        p_id:               id,
        p_user_id:          payload.userId ?? userId,
        p_effective_from:   payload.effectiveFrom,
        p_effective_to:     payload.effectiveTo || null,
        p_job_role:         payload.jobRole,
        p_job_level:        payload.jobLevel ?? 0,
        p_office_location:  payload.officeLocation || null,
        p_contracted_hours: payload.contractedHours ?? null,
        p_source:           payload.source || 'manual',
        p_confidence:       payload.confidence || 'high',
        p_notes:            payload.notes || null,
        p_nl_schaal:        payload.nlSchaal || null,
        p_nl_trede:         payload.nlTrede ?? null,
        p_logging_mode:     payload.loggingMode || (payload.isFreelancer ? 'freelancer' : 'self'),
        p_auto_allocated_project_id: payload.autoAllocatedProjectId ?? null,
        p_auto_allocated_task_id:    payload.autoAllocatedTaskId ?? null,
      });

      if (rpcError) throw rpcError;

      const result = data;
      if (!result?.success) {
        return { success: false, error: result?.error || 'Unknown RPC error' };
      }

      debugLog('Updated row:', id);
      await fetchRoleHistory();
      return { success: true };
    } catch (err) {
      const isOverlap = err?.message?.includes('exclusion constraint') || err?.message?.includes('daterange_excl');
      const msg = isOverlap
        ? 'Date range overlaps with another role history entry. Adjust the dates to avoid overlap.'
        : formatSupabaseError(err, { action: 'update', entity: 'role history row' });
      console.error('[useUserRoleHistory] Update error:', err);
      return { success: false, error: msg };
    }
  }, [userId, fetchRoleHistory]);

  // --- Delete ---
  const deleteRoleHistoryRow = useCallback(async (id) => {
    debugLog('Deleting row:', id);
    try {
      await requireSession();

      const { data, error: rpcError } = await supabase.rpc('role_history_delete_row', {
        p_id:      id,
        p_user_id: userId,
      });

      if (rpcError) throw rpcError;

      const result = data;
      if (!result?.success) {
        return { success: false, error: result?.error || 'Unknown RPC error' };
      }

      debugLog('Deleted row:', id);
      await fetchRoleHistory();
      return { success: true };
    } catch (err) {
      const msg = formatSupabaseError(err, { action: 'delete', entity: 'role history row' });
      console.error('[useUserRoleHistory] Delete error:', err);
      return { success: false, error: msg };
    }
  }, [userId, fetchRoleHistory]);

  return {
    roleHistory,
    loading,
    error,
    refetch: fetchRoleHistory,
    addRoleHistoryRow,
    updateRoleHistoryRow,
    deleteRoleHistoryRow,
    gaps,
    hasCurrentRow,
    // NEW exports
    getContractedHoursForMonth,
  };
}

// ============================================================================
// STANDALONE UTILITY: getContractedHoursForMonth (for use outside the hook)
// ============================================================================
// For pages that don't use the hook but need to resolve contracted hours
// from a pre-fetched array of role history rows.
//
// Usage:
//   import { getContractedHoursForMonthFromRows } from './useUserRoleHistory';
//   const ch = getContractedHoursForMonthFromRows(roleHistoryRows, '2026-03', 176);
// ============================================================================
export function getContractedHoursForMonthFromRows(roleHistoryRows, monthDate, fallbackHours) {
  const fallback = fallbackHours ?? DEFAULT_CONTRACTED_HOURS;
  if (!roleHistoryRows || roleHistoryRows.length === 0) return fallback;

  const sorted = [...roleHistoryRows].sort((a, b) => {
    const aFrom = a.effectiveFrom || a.effective_from || '';
    const bFrom = b.effectiveFrom || b.effective_from || '';
    return aFrom.localeCompare(bFrom);
  });

  // Normalize: handle both camelCase and snake_case
  const normalized = sorted.map(r => ({
    effectiveFrom:   r.effectiveFrom || r.effective_from,
    effectiveTo:     r.effectiveTo || r.effective_to || null,
    contractedHours: r.contractedHours ?? r.contracted_hours ?? null,
    isFreelancer:    (r.isFreelancer === true) || (r.is_freelancer === true),
  }));

  const overlapping = normalized.filter(entry => entryOverlapsMonth(entry, monthDate));

  if (overlapping.length === 0) {
    if (DEBUG_RH) console.log('[getContractedHoursForMonthFromRows] gap at', monthDate, '-> fallback', fallback);
    return fallback;
  }

  const best = overlapping[overlapping.length - 1];
  return best.contractedHours ?? fallback;
}

// Returns the overlapping role history entry's isFreelancer flag for a month,
// or false if no entry overlaps.
export function isFreelancerForMonthFromRows(roleHistoryRows, monthDate) {
  if (!roleHistoryRows || roleHistoryRows.length === 0) return false;

  const normalized = roleHistoryRows.map(r => ({
    effectiveFrom:   r.effectiveFrom || r.effective_from,
    effectiveTo:     r.effectiveTo || r.effective_to || null,
    isFreelancer:    (r.isFreelancer === true) || (r.is_freelancer === true),
  }));

  const sorted = [...normalized].sort((a, b) =>
    (a.effectiveFrom || '').localeCompare(b.effectiveFrom || '')
  );

  const overlapping = sorted.filter(entry => entryOverlapsMonth(entry, monthDate));
  if (overlapping.length === 0) return false;

  return overlapping[overlapping.length - 1].isFreelancer === true;
}

export default useUserRoleHistory;
